import { c as _c } from "react/compiler-runtime";
import { watch } from "@arcgis/core/core/reactiveUtils";
import Point from "@arcgis/core/geometry/Point";
import { geographicToWebMercator } from "@arcgis/core/geometry/support/webMercatorUtils";
import { WORLD_MAP_INITIAL_ZOOM, WORLD_MAP_SINGLE_ENTITY_ZOOM, WORLD_MAP_VIEW_CENTER_LAT, WORLD_MAP_VIEW_CENTER_LON } from "@components/world-map/world-map-constants";
import { getMainClusterEntities, getWebMercatorGeometries, isPointOnVisibleGlobeSide, projectPointToScreen, runGoTo } from "@components/world-map/world-map-utils";
import { useCallback, useEffect, useRef } from "react";
/**
 * Tracks which map entities are visible in the current viewport.
 * Fires `onVisibleChange` with all entities whose geographic position
 * falls within the viewport extent whenever the map comes to rest after a pan or zoom,
 * and also updates whenever the entities array changes.
 *
 * Uses refs internally to avoid re-registering the ArcGIS watcher on every render.
 *
 * @param viewRef - Ref to the ArcGIS view instance. The watcher is registered once the view is available.
 * @param isViewReady - Whether the ArcGIS view has already been initialized.
 * @param entities - Array of map entities to check against the viewport extent.
 * @param onVisibleChange - Callback invoked with the currently visible entities.
 */
export function useVisibleEntities(viewRef, isViewReady, entities, onVisibleChange) {
  const $ = _c(21);
  const entitiesRef = useRef(entities);
  let t0;
  let t1;
  if ($[0] !== entities) {
    t0 = () => {
      entitiesRef.current = entities;
    };
    t1 = [entities];
    $[0] = entities;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== viewRef) {
    t2 = () => {
      const view = viewRef.current;
      const entitiesToFit = view?.type === "3d" ? getMainClusterEntities(entitiesRef.current) : entitiesRef.current;
      const geometries = getWebMercatorGeometries(entitiesToFit);
      const goToOptions = {
        animate: false
      };
      const orientation = view?.type === "3d" ? {
        heading: 0,
        tilt: 0
      } : undefined;
      if (geometries.length === 0) {
        runGoTo(view?.goTo({
          center: [WORLD_MAP_VIEW_CENTER_LON, WORLD_MAP_VIEW_CENTER_LAT],
          zoom: WORLD_MAP_INITIAL_ZOOM,
          ...orientation
        }, goToOptions));
        return;
      }
      if (geometries.length === 1) {
        runGoTo(view?.goTo({
          target: geometries[0],
          zoom: WORLD_MAP_SINGLE_ENTITY_ZOOM,
          ...orientation
        }, goToOptions));
        return;
      }
      runGoTo(view?.goTo({
        target: geometries,
        ...orientation
      }, goToOptions));
    };
    $[3] = viewRef;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const viewAllEntities = t2;
  let t3;
  if ($[5] !== isViewReady || $[6] !== onVisibleChange || $[7] !== viewRef) {
    t3 = () => {
      const view_0 = viewRef.current;
      if (!isViewReady || !view_0 || !onVisibleChange) {
        return;
      }
      try {
        if (view_0.type === "3d") {
          const visibleEntities = entitiesRef.current.filter(entity => {
            const geoPoint = new Point({
              longitude: entity.position.lng,
              latitude: entity.position.lat,
              spatialReference: {
                wkid: 4326
              }
            });
            const webMercatorPoint = geographicToWebMercator(geoPoint);
            return isPointOnVisibleGlobeSide(view_0, geoPoint) && projectPointToScreen(view_0, webMercatorPoint) !== null;
          });
          onVisibleChange(visibleEntities);
          return;
        }
        const extent = view_0.extent;
        if (!extent) {
          return;
        }
        const normalizedExtents = extent.normalize();
        const visibleEntities_0 = [];
        entitiesRef.current.forEach(entity_0 => {
          const geoPoint_0 = new Point({
            longitude: entity_0.position.lng,
            latitude: entity_0.position.lat,
            spatialReference: {
              wkid: 4326
            }
          });
          const point = geographicToWebMercator(geoPoint_0);
          const isVisible = normalizedExtents.some(extent_0 => extent_0.contains(point));
          if (isVisible) {
            visibleEntities_0.push(entity_0);
          }
        });
        onVisibleChange(visibleEntities_0);
      } catch {}
    };
    $[5] = isViewReady;
    $[6] = onVisibleChange;
    $[7] = viewRef;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const calculateVisibleEntities = t3;
  let t4;
  if ($[9] !== calculateVisibleEntities) {
    t4 = () => {
      calculateVisibleEntities();
    };
    $[9] = calculateVisibleEntities;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== calculateVisibleEntities || $[12] !== entities) {
    t5 = [entities, calculateVisibleEntities];
    $[11] = calculateVisibleEntities;
    $[12] = entities;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== calculateVisibleEntities || $[15] !== isViewReady || $[16] !== viewRef) {
    t6 = () => {
      const view_1 = viewRef.current;
      if (!isViewReady || !view_1) {
        return;
      }
      const handle = watch(() => [view_1.stationary, view_1.width, view_1.height], () => {
        if (view_1.stationary) {
          calculateVisibleEntities();
        }
      });
      return () => handle.remove();
    };
    t7 = [isViewReady, calculateVisibleEntities, viewRef];
    $[14] = calculateVisibleEntities;
    $[15] = isViewReady;
    $[16] = viewRef;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  if ($[19] !== viewAllEntities) {
    t8 = {
      viewAllEntities
    };
    $[19] = viewAllEntities;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}